import React, { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';

import ButtonMC from '../../components/ButtonMC';
import DatePickerMC from '../../components/DatePickerMC';
import IconMC from '../../components/IconMC';
import TitleMC from '../../components/TitleMC';
import CalendarEventModalMC from '../../components/CalendarEventModalMC';
import CalendarEventShowModalMC from '../../components/CalendarEventShowModalMC';
import ConfirmDeleteModalMC from '../../components/ConfirmDeleteModalMC';
import { Z_INDEX_CLASS } from '../../../constants/zIndex';
import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import {
  getBrowserTimezone,
  getRetailerTimezone,
  getTimezoneLabel,
  CALENDAR_SLOT_LABEL_FORMAT
} from './calendarTimezoneUtils';
import { computeScrollTime, renderCompactEventContent } from './calendarEventUtils';
import useCalendarEventsManager from './useCalendarEventsManager';
import './fullCalendarMercately.css';

const SIDEBAR_PLUGINS = [timeGridPlugin, interactionPlugin];

const SIDEBAR_SLOT_DURATION = '00:30:00';

const CalendarSidebarModuleMC = () => {
  const { t, i18n } = useTranslation('CalendarEventsScreenMC');
  const { slug: routeSlug } = useParams();
  const slug = routeSlug;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timezone = useMemo(() => (
    getBrowserTimezone() || getRetailerTimezone()
  ), []);
  const timezoneLabel = useMemo(() => getTimezoneLabel(timezone), [timezone]);
  const initialDate = useMemo(() => new Date(), []);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [visibleDate, setVisibleDate] = useState(initialDate);
  const datePickerMethods = useForm({
    defaultValues: {
      sidebarDate: initialDate
    }
  });

  const visibleDateLabel = useMemo(() => {
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: timezone
    }).format(visibleDate);
  }, [i18n.language, timezone, visibleDate]);
  const {
    slug: resolvedSlug,
    modalOpen,
    closeCreateModal,
    selectedEvent,
    defaultStart,
    defaultEnd,
    calendarApi,
    setCalendarApi,
    showEvent,
    setShowEvent,
    calendarEvents,
    handleRangeSelect,
    handleDateClick,
    handleEventClick,
    handleSave,
    handleDelete,
    isDeleteConfirmOpen,
    deleteLoading,
    confirmDelete,
    closeDeleteConfirm,
    handleEventDrop,
    handleEventResize,
    eventDidMount,
    openEditFromShow,
    deleteFromShow
  } = useCalendarEventsManager({
    slug,
    t,
    slotDuration: SIDEBAR_SLOT_DURATION,
    defaultCreateDurationHours: 1
  });

  const renderEventContent = useCallback((arg) => renderCompactEventContent(arg), []);
  const scrollTime = useMemo(() => computeScrollTime(), []);

  const handleDatePickerChange = useCallback((date) => {
    if (!date) return;
    calendarApi?.gotoDate(date);
    setVisibleDate(date);
    setShowDatePicker(false);
  }, [calendarApi]);

  const openFullAgenda = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'CalendarSidebarModuleMC' }
    });
    navigate(`/retailers/${resolvedSlug}/calendar_events`);
  }, [dispatch, navigate, resolvedSlug]);

  const headerToolbar = useMemo(() => ({
    start: 'today',
    center: 'title',
    end: 'prev,next'
  }), []);

  const buttonText = useMemo(() => ({ today: t('actions.today') }), [t]);
  const titleFormat = useMemo(() => ({ weekday: 'long', day: 'numeric', month: 'long' }), []);
  const handleSidebarDatesSet = useCallback(({ view }) => {
    setCalendarApi(view.calendar);
    const activeDate = view.calendar.getDate();
    setVisibleDate(activeDate);
    datePickerMethods.setValue('sidebarDate', activeDate);
  }, [datePickerMethods, setCalendarApi]);

  return (
    <div className="tw-wrapper tw-flex tw-flex-col tw-h-full tw-bg-white">
      <div className="tw-relative tw-flex tw-items-start tw-justify-between tw-gap-2 tw-px-4 tw-pt-4 tw-pb-3 tw-border-b tw-border-gray-100">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <TitleMC
            text={t('sidebar_title')}
            variant="H6"
            textClassName="tw-font-semibold"
          />
          <button
            type="button"
            className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-text-left"
            onClick={() => setShowDatePicker((prev) => !prev)}
          >
            {visibleDateLabel}
          </button>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            className="tw-p-1.5 tw-rounded-md hover:tw-bg-gray-100"
            onClick={openFullAgenda}
            aria-label={t('header.view_full_agenda')}
          >
            <IconMC name="external-link-outline" className="tw-size-4 tw-text-gray-600" />
          </button>
          <ButtonMC
            text={t('header.view_full_agenda')}
            variant="SECONDARY"
            size="small"
            outlined
            onClick={openFullAgenda}
          />
        </div>

        {showDatePicker && (
          <div className={`tw-absolute tw-left-4 tw-top-[72px] ${Z_INDEX_CLASS.calendarSidebarDatePicker} tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-xl tw-p-3 tw-w-[280px]`}>
            <FormProvider {...datePickerMethods}>
              <DatePickerMC
                name="sidebarDate"
                label={t('fields.starts_at')}
                dateFormat="P"
                onChange={handleDatePickerChange}
              />
            </FormProvider>
          </div>
        )}
      </div>

      <div className="tw-flex-1 tw-px-4 tw-py-3">
        <div
          className="fc-mercately-calendar tw-h-full"
          style={{ '--fc-timezone-label': `'${timezoneLabel}'` }}
        >
          <FullCalendar
            locale={esLocale}
            plugins={SIDEBAR_PLUGINS}
            initialView="timeGridDay"
            initialDate={initialDate}
            headerToolbar={headerToolbar}
            buttonText={buttonText}
            titleFormat={titleFormat}
            selectable
            selectMirror
            selectOverlap
            editable
            eventDragMinDistance={8}
            eventResizableFromStart
            events={calendarEvents}
            select={handleRangeSelect}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            allDaySlot={false}
            eventMinHeight={22}
            nowIndicator
            scrollTime={scrollTime}
            slotDuration={SIDEBAR_SLOT_DURATION}
            slotLabelFormat={CALENDAR_SLOT_LABEL_FORMAT}
            eventContent={renderEventContent}
            eventDidMount={eventDidMount}
            height="100%"
            datesSet={handleSidebarDatesSet}
          />
        </div>
      </div>

      <CalendarEventModalMC
        isOpen={modalOpen}
        event={selectedEvent}
        defaultStart={defaultStart}
        defaultEnd={defaultEnd}
        onSave={handleSave}
        onDelete={handleDelete}
        onClose={closeCreateModal}
      />

      <CalendarEventShowModalMC
        isOpen={Boolean(showEvent)}
        event={showEvent}
        onClose={() => setShowEvent(null)}
        onEdit={openEditFromShow}
        onDelete={deleteFromShow}
      />

      <ConfirmDeleteModalMC
        visible={isDeleteConfirmOpen}
        loading={deleteLoading}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CalendarSidebarModuleMC;
